'use client'

import Link from 'next/link'

import { Icon } from '@/components/ui/icon'

interface MosaicProject {
  id: string
  title: string | null
  slug: string
  description: string
  category: { id: string; name: string; slug: string } | null
  media: { id: string; url: string; alt: string | null }[]
}

interface PortfolioMosaicProps {
  projects: MosaicProject[]
}

function ProjectCard({
  project,
  className,
  imageClassName,
  showDescription,
}: {
  project: MosaicProject
  className?: string
  imageClassName?: string
  showDescription?: boolean
}) {
  const image = project.media[0]

  return (
    <Link
      href={`/projects/${project.slug}`}
      className={`group block overflow-hidden rounded-2xl border border-border bg-surface ${className ?? ''}`}
      aria-label={`View project: ${project.title || 'Untitled'}`}
    >
      {/* Image area */}
      <div className={`relative overflow-hidden bg-overlay ${imageClassName ?? ''}`}>
        {image ? (
          <img
            src={image.url}
            alt={image.alt || project.title || 'Project'}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-sm text-muted">
            No image
          </div>
        )}
      </div>
      {/* Text content below image */}
      <div className="p-3 sm:p-4">
        {project.category && (
          // Teal fill, white label (2026-09-08 brand-recolour handoff,
          // explicitly supersedes the earlier sky treatment: "Category tag
          // changes from the pale sky pill to a teal fill pill").
          <span className="mb-1 inline-block rounded-full bg-[color:var(--cw-lime)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[1px] text-white">
            {project.category.name}
          </span>
        )}
        <h3 className="cw-display-3 line-clamp-1 text-foreground">
          {project.title || 'Untitled'}
        </h3>
        {showDescription && (
          <p className="mt-0.5 line-clamp-1 text-xs text-muted">
            {project.description}
          </p>
        )}
      </div>
    </Link>
  )
}

export function PortfolioMosaic({ projects }: PortfolioMosaicProps) {
  if (projects.length === 0) return null

  const [hero, ...rest] = projects
  const remaining = rest.slice(0, 4)

  return (
    <section id="portfolio" className="py-12">
      <div className="mx-auto max-w-[1200px] px-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="cw-display-2 text-foreground">Featured Projects</h2>
          <Link
            href="/projects"
            className="inline-flex items-center gap-1 text-sm font-semibold text-foreground hover:underline"
          >
            View All
            <Icon icon="cw-arrow-right" size="sm" />
          </Link>
        </div>

        {/* 2-column grid: hero spans both columns, rest fill 2×2 */}
        <div className="grid grid-cols-2 gap-2">
          <ProjectCard
            project={hero}
            imageClassName="aspect-[2/1]"
            showDescription
            className="col-span-2"
          />
          {remaining.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              imageClassName="aspect-[2/1]"
            />
          ))}
        </div>
      </div>
    </section>
  )
}
